<script setup>

const url = useRequestURL()
const { locale } = useI18n()
const router = useRouter()
const localePath = useLocalePath()

const { data: page } = await useAPIFetch('License')
const offerings = page.value.body.find(item => item.component === 'LicenseOfferingList')
const references = page.value.body.find(item => item.specific === 'References')
const talents = page.value.body.find(item => item.specific === 'Talents')
const playlists = page.value.body.find(item => item.specific === 'Playlists')

const isLoading = ref(false)

const referencesCursor = ref(references?.cursor)
const referencesItems = ref(references?.items)

const addToReferences = async () => {
	isLoading.value = true

	const result = await useUIFetch(locale.value, `License/component/${references.id}`, {
		'cursor': referencesCursor.value,
		'limit': references.params.limit || 6,
	})

	referencesItems.value = [...referencesItems.value, ...result.items]

	referencesCursor.value = result.cursor
	isLoading.value = false
}

const talentsCursor = ref(talents?.cursor)
const talentsItems = ref(talents?.items)

const addToTalents = async () => {
	isLoading.value = true

	const result = await useUIFetch(locale.value, `License/component/${talents.id}`, {
		'cursor': talentsCursor.value,
		'limit': talents.params.limit || 3,
	})

	talentsItems.value = [...talentsItems.value, ...result.items]

	talentsCursor.value = result.cursor
	isLoading.value = false
}


const interestMusic = page.value.body.find(item => item.specific === 'Interest')?.music
const interestOther = page.value.body.find(item => item.specific === 'Interest')?.other

const form = ref(null)

const scrollToForm= () => {
  form?.value?.scrollIntoView({ behavior: 'smooth' })
}

const onClick = (event) => {
	if (event.target.getAttribute('href')) {
		const href = event.target.getAttribute('href')

		if (href.includes('/alba/') || href.includes('/albums/')) {
			const hrefParts = href.split('/')
			router.push(
				localePath({
					name: 'album-slug-audio',
					params: {
						slug: hrefParts[2],
						audio: hrefParts[3]
					}
				})
			)
		} else if(href.includes('#')) {
			const hash = href.split('#')
			window.scrollTo({
				top: document.getElementById(hash[1]).offsetTop,
				left: 0,
				behavior: 'smooth',
			})
		}
	}
}

useHeadData({
	title: page.value.meta.title,
	description: page.value.meta.description,
	author: page.value.meta.author,
	copyright: page.value.meta.copyright,
	keywords: page.value.meta.keywords,
	og: page.value.meta.og,
	url: url.href
})

</script>

<template>
	<div class="container">
		<h1 class="text-green xl:text-center max-w-[800px] mx-auto mb-5 xl:mb-10">{{ page.attrs.title }}</h1>
		<div class="max-w-[865px] mx-auto xl:text-center mb-14 xl:mb-32">
			<WysiwygPerex :value="page.attrs.perex" />
			<Button @click="scrollToForm" type="green">{{ $t('license.btn') }}</Button>
		</div>

		<div v-if="offerings" class="xl:grid grid-cols-12 gap-8 mb-8 xl:mb-12">
			<div class="col-span-8 col-start-3">
				<div class="xl:text-center">
					<h2 class="text-green mb-8 xl:mb-16">{{ offerings.title }}</h2>
					<div v-for="(offering, index) in offerings.items" :key="index" class="mb-8 xl:mb-12">
						<h5 class="mb-3 xl:mb-4 dark:text-light">{{ offering.title }}</h5>
						<ClientOnly>
							<div v-html="offering.description" @click.prevent="onClick" class="dark:text-lightText"></div>
						</ClientOnly>
					</div>
				</div>
			</div>
		</div>

		<div v-if="references && referencesItems.length" id="References" class="xl:grid grid-cols-12 gap-8 mb-10 xl:mb-20">
			<div class="col-span-12">
				<h2 class="text-green mb-8 xl:mb-16">{{ references.title }}</h2>
				<Grid :covers="referencesItems" :loading="isLoading" noPadding />
				<LoadMore v-if="referencesCursor" smallGrid @click="addToReferences" :loading="isLoading" />
			</div>
		</div>

		<Playlists id="Playlists" v-if="playlists?.items?.length" :playlists="playlists.items" />

		<div v-if="talents" id="Talents" class="xl:grid grid-cols-12 gap-8 mb-8 xl:mb-12">
			<div class="col-span-6">
				<h2 class="text-green mb-8">{{ talents.title }}</h2>
				<div class="dark:text-lightText text-base xl:text-2xl mb-6 xl:mb-8" v-html="talents.perex" />
			</div>
			<div class="col-span-12">
				<Grid :covers="talentsItems" :loading="isLoading" noPadding />
				<LoadMore v-if="talentsCursor" smallGrid @click="addToTalents" :loading="isLoading" />
			</div>
		</div>

		<div ref="form" id="interest" class="xl:grid grid-cols-12 gap-8 mb-10 xl:mb-20">
			<div v-if="interestMusic" class="col-span-6 mb-12 xl:mb-0">
				<h4 class="text-green mb-6 xl:mb-8">{{ interestMusic.title }}</h4>
				<div class="dark:text-lightText mb-8 xl:mb-12 xl:text-lg" v-html="interestMusic.description"></div>
				<FormLicense />
			</div>
			<div v-if="interestOther" class="col-span-4 col-start-9">
				<h4 class="text-green mb-6 xl:mb-8">{{ interestOther.title }}</h4>
				<Contact
					v-for="(item, index) in interestOther.contacts"
					class="mb-6 xl:mb-8"
					:key="index"
					:name="item.attrs.name"
					:surname="item.attrs.surname"
					:title="item.attrs.title"
					:email="item.attrs.email"
					:phone="item.attrs.phone"
					color="green"
				/>
			</div>
		</div>

		<ClientOnly>
			<div class="mb-16 xl:mb-24">
				<FormNewsletter :page="true" />
			</div>
		</ClientOnly>

	</div>
</template>